import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getRoleRepository, useHasAnySuperAdmin } from "@/lib/admin/role-repository";
import { logAction } from "@/lib/admin/audit/log";
import { GoldButton, OutlineButton } from "@/components/admin/AdminShell";

export const Route = createFileRoute("/setup")({
  head: () => ({ meta: [
    { title: "System Initialization — CB16" },
    { name: "description", content: "One-time initialization of the CB16 administration workspace." },
    { name: "robots", content: "noindex" },
  ]}),
  component: SetupWizard,
});

type Step = "check" | "welcome" | "confirm" | "done" | "closed" | "signin";

function SetupWizard() {
  const navigate = useNavigate();
  const { user, ready, isAuthenticated } = useAuth();
  const superState = useHasAnySuperAdmin();
  const [step, setStep] = useState<Step>("check");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready || !superState.ready) return;
    if (superState.exists) { setStep("closed"); return; }
    if (!isAuthenticated) { setStep("signin"); return; }
    setStep((s) => (s === "check" ? "welcome" : s));
  }, [ready, superState.ready, superState.exists, isAuthenticated]);

  async function initialize() {
    if (!user) return;
    setBusy(true); setError(null);
    try {
      const repo = getRoleRepository();
      if (await repo.hasAnySuperAdmin()) { setStep("closed"); return; }
      await repo.assignRole(user.id, "super_admin", user.email ?? null);
      logAction({
        actorId: user.id, actorEmail: user.email ?? undefined,
        entity: "role", entityId: user.id, action: "system_initialize",
        summary: `System initialized — ${user.email ?? user.id} claimed Super Admin`,
        before: null, after: { roles: ["super_admin"] },
      });
      setStep("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Initialization failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="mx-auto max-w-xl px-6 py-24">
        <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">
          System Initialization
        </p>
        <h1 className="mt-3 text-4xl text-black" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
          Welcome to CB16 Atelier
        </h1>
        <div className="mt-2 h-px w-16 bg-[#C6A15B]" />

        {step === "check" && (
          <p className="mt-8 text-sm text-black/60">Checking system state…</p>
        )}

        {step === "signin" && (
          <>
            <p className="mt-8 text-sm text-black/70">
              The workspace has not been initialized. Sign in with the account that should become the first Super Admin, then return to this page.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/login" search={{ redirect: "/setup" }}><GoldButton>Sign in to continue</GoldButton></Link>
              <Link to="/signup" search={{ redirect: "/setup" }}><OutlineButton>Create account</OutlineButton></Link>
            </div>
          </>
        )}

        {step === "welcome" && user && (
          <>
            <p className="mt-8 text-sm text-black/70">
              No Super Admin exists yet in this workspace. As the first authenticated user, you may claim Super Admin. This action is available only once.
            </p>
            <div className="mt-6 border border-black/10 p-4">
              <p className="text-[11px] uppercase tracking-[0.24em] text-black/50">You are signed in as</p>
              <p className="mt-1 text-sm text-black">{user.email ?? user.id}</p>
              <p className="mt-1 font-mono text-[11px] text-black/40">{user.id}</p>
            </div>
            <div className="mt-8 flex gap-3">
              <GoldButton onClick={() => setStep("confirm")}>Continue</GoldButton>
              <OutlineButton onClick={() => navigate({ to: "/" })}>Cancel</OutlineButton>
            </div>
          </>
        )}

        {step === "confirm" && user && (
          <>
            <p className="mt-8 text-sm text-black/70">
              You are about to claim <strong>Super Admin</strong> for this workspace. Once initialized, this page is permanently disabled. Additional administrators can then be promoted from the Admin Portal.
            </p>
            <ul className="mt-4 space-y-1 text-xs text-black/60">
              <li>· Grants unrestricted access to the administration console.</li>
              <li>· Recorded in the Audit Log with your account and timestamp.</li>
              <li>· Cannot be reversed from this wizard.</li>
            </ul>
            {error && <p className="mt-4 text-xs text-red-600">{error}</p>}
            <div className="mt-8 flex gap-3">
              <GoldButton onClick={initialize} disabled={busy}>
                {busy ? "Initializing…" : "Claim Super Admin"}
              </GoldButton>
              <OutlineButton onClick={() => setStep("welcome")} disabled={busy}>Back</OutlineButton>
            </div>
          </>
        )}

        {step === "done" && (
          <>
            <p className="mt-8 text-sm text-black/70">
              Initialization complete. You now hold the Super Admin role. This wizard is disabled.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/admin"><GoldButton>Enter Admin Portal</GoldButton></Link>
              <Link to="/"><OutlineButton>Return home</OutlineButton></Link>
            </div>
          </>
        )}

        {step === "closed" && (
          <>
            <p className="mt-8 text-sm text-black/70">
              This workspace has already been initialized. The initialization wizard is permanently disabled. Contact your Super Admin to request access.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/admin"><GoldButton>Open Admin Portal</GoldButton></Link>
              <Link to="/"><OutlineButton>Return home</OutlineButton></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}